import { slugifyProductName, cleanProductName } from '~~/utils'

const SET_ITEMS = 'SET_ITEMS'
const SET_BAG = 'SET_BAG'
const SET_TOTALS = 'SET_TOTALS'
const SET_SAMPLE_ITEMS = 'SET_SAMPLE_ITEMS'

export const state = () => ({
  items: [],
  sampleItems: [],
  totals: {}
})

export const actions = {
  async add({ commit, dispatch, getters }, id) {
    const bagItem = getters.getBagItem(id)

    if (bagItem) {
      return dispatch('update', { id, quantity: bagItem.quantity + 1 })
    }

    try {
      const response = await this.$axios.$post(`/api/cart`, {
        id,
        quantity: 1
      })

      commit(SET_BAG, response)

      const item = response.items.find(i => i.id == id)
      const name = cleanProductName(item.name)

      if (['original', 'variant'].includes(item.type)) {
        this.$bus.$emit('add-cart', item)

        this.$amplitude.log('addedToCart', {
          productId: item.id,
          name,
          quantity: 1
        })
      }

      this.$announce(`${name} added to bag. Subtotal is $${getters.subtotal}`)

      return response
    } catch (err) {
      dispatch('ui/setAppMessage', err.response.data, { root: true })

      throw err
    }
  },

  async remove({ commit, dispatch, getters, rootGetters }, payload) {
    const isSubscriber = rootGetters['user/isSubscriber']
    if (payload.quantity) {
      return
    }

    const id = payload.id || payload
    const item = getters.getBagItem(id)
    const name = cleanProductName(item.name)

    try {
      const response = await this.$axios.$put(`/api/cart`, {
        id,
        quantity: 0
      })

      // TEMP
      if (['original', 'variant'].includes(item.type) && !isSubscriber) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({
          event: 'eec.remove',
          ecommerce: {
            remove: {
              products: [
                {
                  name: cleanProductName(item.name),
                  id: item.id,
                  price: item.price,
                  quantity: item.quantity
                }
              ]
            }
          }
        })

        this.$amplitude.log('removedFromCart', {
          productId: item.id,
          name: cleanProductName(item.name),
          quantity: item.quantity
        })
      }

      commit(SET_BAG, response)

      this.$announce(
        `Removed ${name} from bag. Subtotal is $${getters.subtotal}`
      )

      return response
    } catch (err) {
      dispatch('ui/setAppMessage', err.response.data, { root: true })

      if (err.response.status !== 400) throw err
    }
  },

  async update({ commit, dispatch, getters }, payload) {
    const { id, quantity } = payload

    if (!payload.quantity) {
      return dispatch('remove', payload)
    }

    try {
      const response = await this.$axios.$put(`/api/cart`, { id, quantity })

      const item = response.items.find(i => i.id === id)
      const name = cleanProductName(item.name)

      commit(SET_BAG, response)

      this.$announce(
        `Updated quantity of ${name} to ${item.quantity} in bag. Subtotal is $${getters.subtotal}`
      )

      return response
    } catch (err) {
      dispatch('ui/setAppMessage', err.response.data, { root: true })

      throw err
    }
  },

  listrakEvent({ state, getters }) {
    if (!window._ltk) {
      return
    }

    if (getters.products.length) {
      for (const item of getters.products) {
        window._ltk.SCA.AddItemWithLinks(
          item.id,
          item.quantity,
          item.amount,
          cleanProductName(item.name),
          `/assets/${item.primaryImage}`,
          `/product/${item.id}/${slugifyProductName(item.name)}`
        )
      }
    } else {
      window._ltk.SCA.ClearCart()
    }

    window._ltk.SCA.Submit()
  },

  setTotals({ commit }, payload) {
    commit(SET_TOTALS, payload)
  },

  setBag({ commit }, payload) {
    commit(SET_BAG, payload)
  },

  setSampleItems({ commit }, payload) {
    commit(SET_SAMPLE_ITEMS, payload)
  }
}

export const mutations = {
  [SET_ITEMS](state, payload) {
    state.items = payload
  },
  [SET_BAG](state, { items, totals }) {
    state.items = items
    state.totals = totals
  },
  [SET_TOTALS](state, payload) {
    state.totals = payload
  },
  [SET_SAMPLE_ITEMS](state, payload) {
    state.sampleItems = payload
  }
}

export const getters = {
  subtotal(state) {
    return state.totals.subtotal
  },

  bagCount(state) {
    return state.items
      .filter(i => ['original', 'variant'].includes(i.type))
      .reduce((acc, cur) => acc + cur.quantity, 0)
  },

  items(state, _, rootState) {
    return state.items.map(i => {
      const originalProduct = rootState.products.products.find(
        p => p.id === i.id
      )

      return {
        ...i,
        amount: originalProduct.amount,
        price: originalProduct.price
      }
    })
  },

  getBagItem(state) {
    return function (productId) {
      return state.items.find(item => item.id == productId)
    }
  },

  isInBag(_, getters) {
    return function (productId) {
      return Boolean(getters.getBagItem(productId))
    }
  },

  sampleInBag(state) {
    return state.items.find(item => item.isSample)
  },

  samples(state) {
    return state.items.filter(item => item.type === 'sample')
  },

  products(state) {
    return state.items.filter(item =>
      ['original', 'variant'].includes(item.type)
    )
  },

  gifts(state) {
    return state.items.filter(item => item.type === 'gift')
  }
}
